import { api, get, post } from '@/lib/api';
import type {
  ErroConhecido,
  ErroConhecidoListado,
  LinhaDeErroConhecido,
  ResultadoDaAplicacao,
  SituacaoDeErro,
  SugestaoDeErro,
} from '@/features/kedb/tipos';

/**
 * Acesso à Base de Erros Conhecidos.
 *
 * Uma função por rota de `kedb.controller.ts`, sem nenhuma inventada.
 *
 * ## Duas buscas diferentes, e a tela não deve confundi-las
 *
 *   • **`GET /known-errors?q=`** é `ILIKE` sobre título, sintomas e código. É a
 *     busca da estante: literal, previsível, boa para achar o registro que se
 *     sabe que existe;
 *   • **`GET /tickets/:id/known-errors/suggestions`** é a busca do atendimento,
 *     e é outra coisa: índice de texto completo em português — com stemming e
 *     remoção de stopword — misturado com identidade de item de configuração e
 *     de serviço. Ela responde "já sabemos disto?" sem que ninguém digite nada.
 *
 * Chamar a primeira de "busca inteligente" na tela seria mentira; chamar a
 * segunda de "busca" seria desperdício. Elas aparecem em lugares diferentes.
 */

const RAIZ = '/known-errors';

const patch = <T>(caminho: string, corpo: unknown) =>
  api<T>(caminho, { method: 'PATCH', body: corpo });

export function listarErrosConhecidos(filtro: { status?: SituacaoDeErro; q?: string } = {}) {
  const busca = new URLSearchParams();
  if (filtro.status) busca.append('status', filtro.status);
  if (filtro.q && filtro.q.trim() !== '') busca.append('q', filtro.q.trim());
  const consulta = busca.toString();
  return get<ErroConhecidoListado[]>(consulta ? `${RAIZ}?${consulta}` : RAIZ);
}

export const obterErroConhecido = (id: string) => get<ErroConhecido>(`${RAIZ}/${id}`);

export interface NovoErroConhecido {
  problemTicketId: string;
  title: string;
  symptoms: string;
  workaround: string;
  rootCause?: string;
  affectedServiceIds?: string[];
  affectedCiIds?: string[];
}

/**
 * `code` e `occurrenceCount` não são aceitos, e por motivos diferentes: o
 * primeiro sai do esquema de identificadores; o segundo é mantido por trigger,
 * e aceitá-lo daria à aplicação um jeito de mentir sobre o dado que justifica
 * priorizar a correção definitiva.
 */
export const criarErroConhecido = (corpo: NovoErroConhecido) =>
  post<LinhaDeErroConhecido>(RAIZ, corpo);

export interface AlteracaoDeErroConhecido {
  title?: string;
  symptoms?: string;
  workaround?: string;
  rootCause?: string;
  affectedServiceIds?: string[];
  affectedCiIds?: string[];
  status?: SituacaoDeErro;
  /**
   * Precisa ser uma **mudança**: é ela que elimina o erro de vez.
   *
   * `null` desvincula, e é um valor legítimo: `@IsOptional()` deixa o nulo
   * passar pela validação, e o serviço só confere o tipo do chamado quando há
   * identificador (`if (patch.permanentFixTicketId)`), gravando `null` como
   * qualquer outro campo. Omitir a chave é outra coisa — é "não mexa nisto".
   */
  permanentFixTicketId?: string | null;
  version?: number;
}

/**
 * Devolve a **linha crua**: nem `_count` nem `problemTicket`, que só o `GET`
 * inclui. Prometer `ErroConhecidoListado` aqui convidaria a costurar a resposta
 * na listagem com `setQueryData`, e a lista quebraria ao ler o número do
 * problema de origem. Quem chama invalida.
 */
export const alterarErroConhecido = (id: string, corpo: AlteracaoDeErroConhecido) =>
  patch<LinhaDeErroConhecido>(`${RAIZ}/${id}`, corpo);

/**
 * Registra o que o operador fez com a sugestão.
 *
 * `applied: false` é tão útil quanto `true`: sugestão recusada é o sinal de que
 * a base está devolvendo ruído, e sem registro ninguém descobre. Só a aplicada
 * conta para o contador e cria o vínculo tipado com o problema.
 */
export const excluirErroConhecido = (id: string) =>
  api<void>(`${RAIZ}/${id}`, { method: 'DELETE' });

export const aplicarContorno = (
  id: string,
  corpo: { ticketId: string; applied?: boolean; note?: string },
) => post<ResultadoDaAplicacao>(`${RAIZ}/${id}/apply`, corpo);

/** ★ A sugestão durante o atendimento — critério 4.15. */
export const obterSugestoes = (ticketId: string, limite = 5) =>
  get<SugestaoDeErro[]>(`/tickets/${ticketId}/known-errors/suggestions?limit=${limite}`);

export const chavesDeKedb = {
  raiz: ['erros-conhecidos'] as const,
  lista: (status: string, q: string) => ['erros-conhecidos', 'lista', status, q] as const,
  item: (id: string) => ['erros-conhecidos', 'item', id] as const,
  /** Sob a raiz de chamados: a sugestão muda quando a classificação muda. */
  sugestoes: (ticketId: string) => ['chamados', 'item', ticketId, 'erros-conhecidos'] as const,
};
